import {
  ChEditCustomEvent,
  TabModel,
  TabSelectedItemInfo,
  TreeViewItemModelExtended,
  TreeViewModel
} from "@genexus/chameleon-controls-library";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import {
  Component,
  Element,
  Fragment,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";
import { Locale } from "../../../common/locale";
import {
  Action,
  CommitCallback,
  CommitData,
  LoadLocalChangesCallback,
  LoadRemoteChangesCallback,
  ModifiedObject,
  ModifiedObjectData,
  ObjectState,
  UpdateCallback,
  UpdateData
} from "../version-control/common/types";

enum TeamDevEmptyState {
  NoKBOpen,
  NoConnectedKB,
  NoConnectedGXServer,
  NoLinkedVersion,
  FrozenVersion,
  None
}

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar",
  "components/tree-view",
  "components/tab"
];

const VERSION_ICON = getIconPath({
  category: "system",
  name: "version",
  colorType: "on-surface"
});

const ADD_ICON = getIconPath({
  category: "system",
  name: "add",
  colorType: "success"
});

const EDITED_ICON = getIconPath({
  category: "system",
  name: "edit",
  colorType: "warning"
});

const SUBTRACT_ICON = getIconPath({
  category: "system",
  name: "substract",
  colorType: "error"
});

const REFRESH_ICON = getIconPath({
  category: "system",
  name: "refresh",
  colorType: "primary"
});

const WARNING_ICON = getIconPath({
  category: "system",
  name: "warning",
  colorType: "warning"
});

const OPEN_WINDOW_ICON = getIconPath({
  category: "gemini-tools",
  name: "open-window",
  colorType: "on-surface"
});

const LOADER_ICON = getIconPath({
  category: "system",
  name: "loading",
  colorType: "primary"
});

const ERROR_ICON = getIconPath({
  category: "system",
  name: "error",
  colorType: "on-surface"
});

const REMOTE_CHANGES_TAB = "remote-changes-tab";
const LOCAL_CHANGES_TAB = "local-changes-tab";

@Component({
  tag: "gx-ide-team-dev-preview",
  styleUrl: "team-dev-preview.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-preview"]
})
export class GxIdeTeamDevPreview {
  #componentLocale: any;
  #isPendingReloadLocalChanges: boolean = false;
  #isPendingReloadRemoteChanges: boolean = false;
  #commitData: CommitData;
  #updateData: UpdateData;
  #prevVersionName: string;
  #isTeamDevOptionsEnabled: boolean = false;
  #emptyStateConfig: { [key: string]: any } = {};
  #localChangesTreeEL: HTMLChTreeViewRenderElement;
  #remoteChangesTreeEL: HTMLChTreeViewRenderElement;

  @Element() el: HTMLGxIdeTeamDevPreviewElement;

  @State() loadingRemoteChanges: boolean = false;
  @State() loadingLocalChanges: boolean = true;
  @State() tabModel: TabModel = [];

  /**
   * Currently selected tab ID
   */
  @State() selectedTabId: string = LOCAL_CHANGES_TAB;

  /**
   * Number of pending commit changes
   */
  @State() pendingCommitChangesCount: number = 0;
  /**
   * Number of pending remote changes
   */
  @State() pendingRemoteChangesCount: number = 0;

  /**
   * Represents the tree view model for remote changes
   */
  @State() remoteChangesTreeViewModel: TreeViewModel = [];

  /**
   * Represents the tree view model for local changes
   */
  @State() localChangesTreeViewModel: TreeViewModel = [];

  /**
   * Represents the selected nodes ids from remote changes tree
   */
  @State() remoteChangesCheckedNodesIds: string[] = [];

  /**
   * Represents the selected nodes ids from local changes tree
   */
  @State() localChangesCheckedNodesIds: string[] = [];

  /**
   * Commit message entered by the user
   */
  @State() commitMessage: string = "";

  /**
   * Indicates if the commit process is ongoing
   */
  @State() committingChanges: boolean = false;

  /**
   * Indicates if the update process is ongoing
   */
  @State() updatingChanges: boolean = false;

  /**
   * Indicates if the server operations (commit/update) should be disabled
   */
  @State() serverOperationsDisabled: boolean = false;

  /**
   * Data required to render the Team Dev Preview
   */
  @Prop() readonly teamDevPreviewData: TeamDevPreviewData;
  @Watch("teamDevPreviewData")
  async onTeamDevPreviewDataChanged() {
    const { versionName } = this.teamDevPreviewData;
    let versionChanged = false;

    this.#setTeamDevOptionsStatus();
    if (this.#prevVersionName !== versionName) {
      this.#prevVersionName = versionName;
      versionChanged = true;
    }

    if (this.#isTeamDevOptionsEnabled && versionChanged) {
      await this.reloadChanges("local");
      await this.reloadChanges("remote");
    }
  }

  /**
   * Invoked to return the list of remote changes
   */
  @Prop() readonly loadRemoteChangesCallback!: LoadRemoteChangesCallback;

  /**
   * Invoked to return the list of local changes
   */
  @Prop() readonly loadLocalChangesCallback!: LoadLocalChangesCallback;

  /**
   * Invoked when user starts the commit process
   */
  @Prop() readonly commitCallback!: CommitCallback;

  /**
   * Invoked when user starts the update process
   */
  @Prop() readonly updateCallback!: UpdateCallback;

  /**
   * Invoked to open the full Team Dev dialog
   */
  @Prop() readonly openFullDialogCallback!: () => Promise<void>;

  /**
   * Invoked to share the knowledge base to the server
   */
  @Prop() readonly shareKBToServerCallback!: () => Promise<void>;

  /**
   * Invoked to create a knowledge base from the server
   */
  @Prop() readonly createKBFromServerCallback!: () => Promise<void>;

  /**
   * Invoked to open the Team Development information
   */
  @Prop() readonly openTeamDevInfoCallback!: () => Promise<void>;

  /**
   * Invoked to reconnect to GeneXus Server
   */
  @Prop() readonly reconnectCallback!: () => Promise<void>;

  async componentWillLoad() {
    this.#setTeamDevOptionsStatus();
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.tabModel = [
      {
        id: LOCAL_CHANGES_TAB,
        name: `${this.#componentLocale.captions.localChanges}`
      },
      {
        id: REMOTE_CHANGES_TAB,
        name: `${this.#componentLocale.captions.remoteChanges}`
      }
    ];
    this.#setEmptyStateConfig();
  }

  async componentDidLoad() {
    if (this.#isTeamDevOptionsEnabled) {
      await this.#loadChanges("local");
      this.#isPendingReloadRemoteChanges = true;
    }
    this.loadingLocalChanges = false;
  }

  /**
   * Used by the host to reload remote or local changes
   */
  @Method()
  async reloadChanges(type: TeamDevPreviewMode) {
    const isLocal = type === "local";
    const isActiveTab = isLocal
      ? this.selectedTabId === LOCAL_CHANGES_TAB
      : this.selectedTabId === REMOTE_CHANGES_TAB;

    if (!isActiveTab) {
      if (isLocal) {
        this.#isPendingReloadLocalChanges = true;
      } else {
        this.#isPendingReloadRemoteChanges = true;
      }
      return;
    }

    isLocal
      ? await this.#loadChanges("local")
      : await this.#loadChanges("remote");
  }

  /**
   * Used by the host to instruct the component to disable the commit/update button.
   */
  @Method()
  async disableServerOperations(value: boolean) {
    this.serverOperationsDisabled = value;
  }

  #setEmptyStateConfig = () => {
    this.#emptyStateConfig = {
      [TeamDevEmptyState.NoKBOpen]: {
        title: this.#componentLocale.emptyState.noConnectedKBTitle,
        description: this.#componentLocale.emptyState.noOpenKBDescription,
        buttonText: this.#componentLocale.buttons.createKBFromServer,
        buttonHandler: this.#createKBFromServerHandler,
        showButton: true
      },
      [TeamDevEmptyState.NoConnectedKB]: {
        title: this.#componentLocale.emptyState.noConnectedKBTitle,
        description: this.#componentLocale.emptyState.noConnectedKBDescription,
        buttonText: this.#componentLocale.buttons.shareKBToServer,
        buttonHandler: this.#shareKBToServerHandler,
        showButton: true
      },
      [TeamDevEmptyState.NoConnectedGXServer]: {
        title: this.#componentLocale.emptyState.noConnectedGXServerTitle,
        description:
          this.#componentLocale.emptyState.noConnectedGXServerDescription,
        buttonText: this.#componentLocale.buttons.reconnect,
        buttonHandler: this.#reconnectHandler,
        showButton: true
      },
      [TeamDevEmptyState.NoLinkedVersion]: {
        title: this.#componentLocale.emptyState.noLinkedVersionTitle,
        description:
          this.#componentLocale.emptyState.noLinkedVersionDescription,
        buttonText: "",
        buttonHandler: undefined,
        showButton: false
      },
      [TeamDevEmptyState.FrozenVersion]: {
        title: this.#componentLocale.emptyState.frozenVersionTitle,
        description: this.#componentLocale.emptyState.frozenVersionDescription,
        buttonText: "",
        buttonHandler: undefined,
        showButton: false
      },
      [TeamDevEmptyState.None]: {
        title: "",
        description: "",
        buttonText: "",
        buttonHandler: undefined,
        showButton: false
      }
    };
  };

  #setTeamDevOptionsStatus = () => {
    if (
      this.teamDevPreviewData.isKBOpen &&
      this.teamDevPreviewData.isLinkedKB &&
      this.teamDevPreviewData.isLinkedVersion &&
      !this.teamDevPreviewData.isFrozenVersion &&
      this.teamDevPreviewData.existsGXServerSession
    ) {
      this.#isTeamDevOptionsEnabled = true;
      return;
    }
    this.#isTeamDevOptionsEnabled = false;
  };

  #loadChanges = async (type: TeamDevPreviewMode) => {
    if (type === "local") {
      this.loadingLocalChanges = true;
      this.#commitData = await this.loadLocalChangesCallback();
      this.#convertDataToTreeView("local");
      this.loadingLocalChanges = false;
      this.#isPendingReloadLocalChanges = false;
    } else {
      this.loadingRemoteChanges = true;
      this.#updateData = await this.loadRemoteChangesCallback();
      this.#convertDataToTreeView("remote");
      this.loadingRemoteChanges = false;
      this.#isPendingReloadRemoteChanges = false;
    }
  };

  #convertDataToTreeView = (type: TeamDevPreviewMode) => {
    let rootItemName = "";
    let data: ModifiedObjectData[] | ModifiedObject[];
    const isLocal = type === "local";
    if (isLocal) {
      this.pendingCommitChangesCount =
        this.#commitData?.pendingForCommit?.length || 0;
      rootItemName = `${this.#componentLocale.captions.localChanges} (${this.pendingCommitChangesCount})`;
      data = this.#commitData.pendingForCommit;
    } else {
      this.pendingRemoteChangesCount =
        this.#updateData?.pendingForUpdate?.length || 0;
      rootItemName = `${this.#componentLocale.captions.remoteChanges} (${this.pendingRemoteChangesCount})`;
      data = this.#updateData.pendingForUpdate;
    }

    const treeViewModel: TreeViewModel = [
      {
        id: "root",
        caption: rootItemName,
        expanded: true,
        parts: "root-item",
        items: data?.map(item => ({
          id: item.id,
          startImgSrc: isLocal
            ? (item as ModifiedObjectData).type.icon
            : (item as ModifiedObject).typeIcon,
          endImgSrc: isLocal
            ? this.#renderIcon((item as ModifiedObjectData).state)
            : this.#renderIcon((item as ModifiedObject).action),
          caption: (
            //WA: Used to apply different styles to the item caption and path
            <div
              style={{
                display: "flex"
              }}
            >
              <span>{item.name}</span>
              <span
                style={{
                  color: "var(--color-text-neutral-disabled)",
                  marginLeft: "4px"
                }}
              >
                {item.module}
              </span>
            </div>
          ),
          leaf: true,
          parts: "change-item"
        }))
      }
    ];

    if (isLocal) {
      this.localChangesTreeViewModel = treeViewModel;
      this.#updateTabModel(LOCAL_CHANGES_TAB);
    } else {
      this.remoteChangesTreeViewModel = treeViewModel;
      this.#updateTabModel(REMOTE_CHANGES_TAB);
    }
  };

  #checkedItemsChangeHandler = async (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>,
    type: TeamDevPreviewMode
  ) => {
    const checkedNodesIds = Array.from(event.detail.values())
      .filter(node => node.item.checked && node.item.id !== "root")
      .map(node => node.item.id);

    if (type === "local") {
      this.localChangesCheckedNodesIds = checkedNodesIds;
      this.#localChangesTreeEL.updateItemsProperties(["root"], {
        caption: `${this.#componentLocale.captions.localChanges} (${this.localChangesCheckedNodesIds.length}/${this.pendingCommitChangesCount})`
      });
    } else {
      this.remoteChangesCheckedNodesIds = checkedNodesIds;
      this.#remoteChangesTreeEL.updateItemsProperties(["root"], {
        caption: `${this.#componentLocale.captions.remoteChanges} (${this.remoteChangesCheckedNodesIds.length}/${this.pendingRemoteChangesCount})`
      });
    }
  };

  #shareKBToServerHandler = async () => {
    await this.shareKBToServerCallback();
  };

  #createKBFromServerHandler = async () => {
    await this.createKBFromServerCallback();
  };

  #openFullDialogHandler = async () => {
    await this.openFullDialogCallback();
  };

  #commitChangesHandler = async () => {
    this.committingChanges = true;
    await this.commitCallback(
      this.localChangesCheckedNodesIds,
      this.commitMessage
    );
    this.commitMessage = "";
    this.committingChanges = false;
  };

  #commitMessageInputHandler = (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.commitMessage = (event.detail as string) || "";
  };

  #updateRemoteChangesHandler = async () => {
    this.updatingChanges = true;
    await this.updateCallback(this.remoteChangesCheckedNodesIds);
    this.updatingChanges = false;
    this.remoteChangesCheckedNodesIds = [];
  };

  #tabSelectionChangedHandler = async (
    event: CustomEvent<TabSelectedItemInfo>
  ): Promise<void> => {
    this.selectedTabId = event.detail.newSelectedId;
    if (
      this.selectedTabId === LOCAL_CHANGES_TAB &&
      this.#isPendingReloadLocalChanges
    ) {
      await this.#loadChanges("local");
    }
    if (
      this.selectedTabId === REMOTE_CHANGES_TAB &&
      this.#isPendingReloadRemoteChanges
    ) {
      await this.#loadChanges("remote");
    }
  };

  #openTeamDevInfoHandler = async () => {
    await this.openTeamDevInfoCallback();
  };

  #reconnectHandler = async () => {
    await this.reconnectCallback();
  };

  #renderIcon = (value: ObjectState | Action) => {
    const iconMap = {
      "inserted": ADD_ICON,
      "modified": EDITED_ICON,
      "deleted": SUBTRACT_ICON,
      "conflicted": WARNING_ICON,
      "modified-with-conflicts": WARNING_ICON
    };
    return iconMap[value] || null;
  };

  #disableConfirmButton = (type: TeamDevPreviewMode): boolean => {
    const isLocal = type === "local";
    if (isLocal) {
      return (
        this.loadingLocalChanges ||
        !this.commitMessage.trim().length ||
        this.localChangesCheckedNodesIds.length === 0 ||
        this.committingChanges ||
        this.serverOperationsDisabled
      );
    } else {
      return (
        this.loadingRemoteChanges ||
        this.remoteChangesCheckedNodesIds.length === 0 ||
        this.updatingChanges ||
        this.serverOperationsDisabled
      );
    }
  };

  #updateTabModel = (tabId: string) => {
    this.tabModel = this.tabModel.map(tab =>
      tab.id === tabId
        ? {
            ...tab,
            name:
              tabId === LOCAL_CHANGES_TAB
                ? `${this.#componentLocale.captions.localChanges} (${this.pendingCommitChangesCount})`
                : `${this.#componentLocale.captions.remoteChanges} (${this.pendingRemoteChangesCount})`
          }
        : tab
    );
  };

  private getEmptyStateType(): TeamDevEmptyState {
    const {
      isFrozenVersion,
      existsGXServerSession,
      isKBOpen,
      isLinkedVersion,
      isLinkedKB
    } = this.teamDevPreviewData;
    if (!isKBOpen) {
      return TeamDevEmptyState.NoKBOpen;
    }
    if (isKBOpen && !isLinkedKB) {
      return TeamDevEmptyState.NoConnectedKB;
    }
    if (isKBOpen && isLinkedKB && !isLinkedVersion && !existsGXServerSession) {
      return TeamDevEmptyState.NoConnectedGXServer;
    }
    if (isKBOpen && isLinkedKB && !isLinkedVersion) {
      return TeamDevEmptyState.NoLinkedVersion;
    }
    if (isKBOpen && isLinkedKB && existsGXServerSession && isFrozenVersion) {
      return TeamDevEmptyState.FrozenVersion;
    }
    if (isKBOpen && isLinkedKB && !existsGXServerSession) {
      return TeamDevEmptyState.NoConnectedGXServer;
    }
    return TeamDevEmptyState.None;
  }

  #getEmptyStateTitle = () =>
    this.#emptyStateConfig[this.getEmptyStateType()].title;

  #getEmptyStateDescription = () =>
    this.#emptyStateConfig[this.getEmptyStateType()].description;

  #getEmptyStateButtonText = () =>
    this.#emptyStateConfig[this.getEmptyStateType()].buttonText;

  #getEmptyStateButtonHandler = () =>
    this.#emptyStateConfig[this.getEmptyStateType()].buttonHandler;

  #renderEmptyStateButton = () =>
    this.#emptyStateConfig[this.getEmptyStateType()].showButton;

  #renderReloadChangesButton = (type: TeamDevPreviewMode) => {
    let isLoading = false;
    let isConfirmationInProcess = false;
    let reloadCaption;
    let reloadingCaption;
    const isLocal = type === "local";

    if (isLocal) {
      isLoading = this.loadingLocalChanges;
      isConfirmationInProcess = this.committingChanges;
      reloadCaption = this.#componentLocale.buttons.reloadLocal;
      reloadingCaption = this.#componentLocale.buttons.reloadingLocal;
    } else {
      isLoading = this.loadingRemoteChanges;
      isConfirmationInProcess = this.updatingChanges;
      reloadCaption = this.#componentLocale.buttons.reloadRemote;
      reloadingCaption = this.#componentLocale.buttons.reloadingRemote;
    }
    return (
      <button
        class="button-tertiary button-icon-and-text body-regular-s refresh-button"
        type="button"
        onClick={
          isLocal
            ? () => this.#loadChanges("local")
            : () => this.#loadChanges("remote")
        }
        disabled={
          isLoading || isConfirmationInProcess || this.serverOperationsDisabled
        }
      >
        <ch-image
          class={{
            "icon-m": true,
            "loader-icon": isLoading
          }}
          src={isLoading ? LOADER_ICON : REFRESH_ICON}
          type="mask"
        />

        {isLoading ? reloadingCaption : reloadCaption}
      </button>
    );
  };

  #renderEmptyState = (type: TeamDevPreviewMode) => {
    let show = false;
    let message = "";

    if (type === "local" && this.pendingCommitChangesCount === 0) {
      show = true;
      message = this.#componentLocale.emptyState.noLocalChanges;
    } else if (type === "remote" && this.pendingRemoteChangesCount === 0) {
      show = true;
      message = this.#componentLocale.emptyState.noRemoteChanges;
    }

    return show ? (
      <div class="empty-state-container">
        <p class="body-regular-s">{message}</p>
        {this.#renderReloadChangesButton(type)}
      </div>
    ) : null;
  };

  #renderTreeChanges = (type: TeamDevPreviewMode) => {
    const isLocal = type === "local";

    if (isLocal) {
      return (
        <Fragment>
          {this.#renderReloadChangesButton(type)}
          {!this.loadingLocalChanges && !this.committingChanges && (
            <ch-tree-view-render
              checkbox
              checked
              class="tree-view"
              model={this.localChangesTreeViewModel}
              toggleCheckboxes
              onCheckedItemsChange={event =>
                this.#checkedItemsChangeHandler(event, type)
              }
              expandableButton="no"
              expandOnClick={false}
              ref={el =>
                (this.#localChangesTreeEL = el as HTMLChTreeViewRenderElement)
              }
            />
          )}
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          {this.#renderReloadChangesButton(type)}
          {!this.loadingRemoteChanges && !this.updatingChanges && (
            <ch-tree-view-render
              checkbox
              checked
              class="tree-view"
              model={this.remoteChangesTreeViewModel}
              toggleCheckboxes
              onCheckedItemsChange={event =>
                this.#checkedItemsChangeHandler(event, type)
              }
              expandableButton="no"
              expandOnClick={false}
              ref={el =>
                (this.#remoteChangesTreeEL = el as HTMLChTreeViewRenderElement)
              }
            />
          )}
        </Fragment>
      );
    }
  };

  #renderConfirmButton = (type: TeamDevPreviewMode) => {
    const isLocal = type === "local";
    let isConfirmationInProcess = false;
    let id = "";
    if (isLocal) {
      isConfirmationInProcess = this.committingChanges;
      id = "commit-changes-button";
    } else {
      isConfirmationInProcess = this.updatingChanges;
      id = "update-remote-changes-button";
    }
    return (
      <button
        id={id}
        class={{
          "button-primary": true,
          "button-icon-and-text": isConfirmationInProcess
        }}
        type="button"
        disabled={this.#disableConfirmButton(type)}
        onClick={
          isLocal
            ? this.#commitChangesHandler
            : this.#updateRemoteChangesHandler
        }
      >
        {isConfirmationInProcess && (
          <ch-image class="icon-m loader-icon" src={LOADER_ICON} type="mask" />
        )}
        {isConfirmationInProcess
          ? isLocal
            ? this.#componentLocale.buttons.committing
            : this.#componentLocale.buttons.updating
          : isLocal
            ? this.#componentLocale.buttons.commit
            : this.#componentLocale.buttons.update}
      </button>
    );
  };

  #renderLocalChanges = () => {
    if (this.pendingCommitChangesCount > 0) {
      return (
        <Fragment>
          <ch-edit
            id="commit-message-input"
            class="input commit-message-textarea"
            value={this.commitMessage}
            accessibleName={this.#componentLocale.accessibleNames.commitMessage}
            placeholder={this.#componentLocale.placeholders.commitMessage}
            disabled={this.loadingLocalChanges}
            onInput={this.#commitMessageInputHandler}
            multiline
          />
          {this.#renderConfirmButton("local")}
          {this.#renderTreeChanges("local")}
        </Fragment>
      );
    }
  };

  #renderRemoteChanges = () => {
    if (this.pendingRemoteChangesCount > 0) {
      return (
        <Fragment>
          {this.#renderConfirmButton("remote")}
          {this.#renderTreeChanges("remote")}
        </Fragment>
      );
    }
  };

  render() {
    const { existsGXServerSession } = this.teamDevPreviewData;
    return (
      <Host
        class={{
          "widget": true,
          "empty-state": !this.#isTeamDevOptionsEnabled
        }}
      >
        <ch-theme model={CSS_BUNDLES} />
        {!this.#isTeamDevOptionsEnabled || !existsGXServerSession ? (
          <gx-ide-empty-state
            isAnimated={true}
            stateIconSrc={ERROR_ICON}
            stateTitle={this.#getEmptyStateTitle()}
            stateDescription={this.#getEmptyStateDescription()}
          >
            {this.#renderEmptyStateButton() && (
              <button
                class="button-primary empty-state-button"
                onClick={this.#getEmptyStateButtonHandler()}
              >
                {this.#getEmptyStateButtonText()}
              </button>
            )}
            <div>
              <p class="body-regular-s">
                {this.#componentLocale.emptyState.about}
              </p>
              <button
                class="button-tertiary button-icon-and-text team-dev-button body-semi-bold-s"
                type="button"
                onClick={this.#openTeamDevInfoHandler}
              >
                {this.#componentLocale.buttons.teamDev}
                <ch-image class="icon-m" src={OPEN_WINDOW_ICON} type="mask" />
              </button>
            </div>
          </gx-ide-empty-state>
        ) : (
          <div class="main-container spacing-body scrollable">
            <p class="giu-static-pill-icon-and-text version giu-static-pill-icon-and-text--version elevation-02">
              <ch-image
                class="icon-m"
                type="mask"
                src={VERSION_ICON}
              ></ch-image>
              <span class="version-pill">
                {this.teamDevPreviewData.versionName}
              </span>
            </p>
            <ch-tab-render
              class="tab tab-full-width-buttons scrollable border-style-none"
              model={this.tabModel}
              selectedId={this.selectedTabId}
              tabListPosition="block-start"
              onSelectedItemChange={this.#tabSelectionChangedHandler}
            >
              <div class="local-changes-container" slot={LOCAL_CHANGES_TAB}>
                {this.#renderEmptyState("local")}
                {this.#renderLocalChanges()}
              </div>

              <div class="remote-changes-container" slot={REMOTE_CHANGES_TAB}>
                {this.#renderEmptyState("remote")}
                {this.#renderRemoteChanges()}
              </div>
            </ch-tab-render>

            <footer class="dialog-footer-with-border open-window-container">
              <div class="buttons-spacer">
                <button
                  class="button-tertiary button-icon-and-text open-full-dialog-button"
                  onClick={this.#openFullDialogHandler}
                >
                  <ch-image class="icon-m" src={OPEN_WINDOW_ICON} type="mask" />
                  {this.#componentLocale.footer.openFullDialog}
                </button>
              </div>
            </footer>
          </div>
        )}
      </Host>
    );
  }
}

export type TeamDevPreviewData = {
  versionName: string;
  isFrozenVersion: boolean;
  existsGXServerSession: boolean;
  isKBOpen: boolean;
  isLinkedVersion: boolean;
  isLinkedKB: boolean;
};

export type TeamDevPreviewMode = "local" | "remote";
